/**
 * Restores the customer's exact scroll position (and, on collection/search
 * pages that use the theme's custom <infinite-scroll> element, however many
 * extra pages of products had already been loaded) when they navigate back
 * to a page they were already on - most commonly: back button from a
 * product page to the collection/search listing they came from.
 *
 * Why this is needed: this theme loads extra pages of a collection/search
 * grid via JS (infinite scroll) without changing the URL. A plain browser
 * back navigation only restores the ORIGINAL page 1 markup, so without this
 * script the customer lands back near the top of the listing having lost
 * everything they scrolled past. This script remembers both the scroll
 * position and how many extra pages were loaded (per URL, in
 * sessionStorage) and replays them before restoring scroll - with no full
 * page reload.
 *
 * This also restores plain scroll position (no infinite-scroll involved)
 * for every other page on the site, since the browser's own "auto" scroll
 * restoration on back/forward is not something this theme can rely on.
 */
(function () {
  'use strict';

  if ('scrollRestoration' in history) {
    try {
      history.scrollRestoration = 'manual';
    } catch (e) {
      /* ignore */
    }
  }

  var STORAGE_PREFIX = 'scrollState::';

  function storageKey() {
    return STORAGE_PREFIX + location.pathname + location.search;
  }

  function getInfiniteScrollEl() {
    return document.querySelector('infinite-scroll');
  }

  function saveState() {
    try {
      var el = getInfiniteScrollEl();
      var state = {
        y: window.scrollY || window.pageYOffset || 0,
        page: el && el.page ? el.page : 1,
      };
      sessionStorage.setItem(storageKey(), JSON.stringify(state));
    } catch (err) {
      /* sessionStorage may be unavailable (private browsing, etc.) - safe to skip */
    }
  }

  var saveScheduled = false;
  function scheduleSave() {
    if (saveScheduled) return;
    saveScheduled = true;
    window.requestAnimationFrame(function () {
      saveScheduled = false;
      saveState();
    });
  }

  window.addEventListener('scroll', scheduleSave, { passive: true });
  window.addEventListener('pagehide', saveState);
  window.addEventListener('beforeunload', saveState);

  // Capture the very latest position/page count right as the customer
  // activates a link (e.g. a product card), rather than relying only on the
  // throttled scroll listener having already fired.
  document.addEventListener(
    'click',
    function (event) {
      var link = event.target && event.target.closest && event.target.closest('a[href]');
      if (link) saveState();
    },
    true
  );

  function restore() {
    var raw;
    try {
      raw = sessionStorage.getItem(storageKey());
    } catch (err) {
      return;
    }
    if (!raw) return;

    var state;
    try {
      state = JSON.parse(raw);
    } catch (err) {
      return;
    }
    if (!state || typeof state.y !== 'number') return;

    function scrollNow() {
      window.scrollTo(0, state.y);
    }

    var el = getInfiniteScrollEl();
    if (el && state.page > 1 && typeof el.restoreToPage === 'function') {
      window.requestAnimationFrame(function () {
        el.restoreToPage(state.page).then(function () {
          window.requestAnimationFrame(scrollNow);
        });
      });
    } else {
      scrollNow();
    }
  }

  window.addEventListener('pageshow', restore);
})();
